import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';

// Phase C.3.2 — global site-shell cleanup. This Footer previously
// presented Football for Nerds as primarily an NFL-analytics product
// ("Advanced NFL analytics for fans...", Product/Stats/Learn columns all
// pointing at /nfl) even while a visitor was on a youth-football Program/
// Division/Team page — the current, primary FFN product. Rewritten to
// lead with the current identity; the legacy NFL/fantasy/college
// experience is deliberately NOT deleted (see App.js's own routes,
// unchanged) — it gets one compact, honest link instead of three whole
// columns.
//
// One shared Footer, no per-mode variants — same component renders on
// the landing page, every public Program/Division/Team page, and the
// legacy NFL/College/Fantasy/Tools pages (the only places this component
// is actually imported today; private Team/Program management pages
// don't render a Footer at all, unchanged by this pass — adding one
// there would be a layout change beyond this phase's narrow branding
// scope).
//
// nflverse attribution ("Data powered by nflverse") is preserved
// verbatim, not deleted — see LEGACY_ANALYTICS_PREFIXES below for why it
// now only renders while browsing the legacy analytics experience that
// actually displays nflverse-sourced data, instead of unconditionally on
// every youth-football Program page that has nothing to do with it.
const LEGACY_ANALYTICS_PREFIXES = ['/nfl', '/teams', '/team/', '/college', '/fantasy', '/tools'];

const LINKS = {
  PLATFORM: [
    { label: 'Find a Program', path: '/programs'    },
    { label: 'My Programs',    path: '/my-programs' },
    { label: 'My Teams',       path: '/my-teams'    },
    { label: 'My Athletes',    path: '/athletes'    },
  ],
  MORE: [
    { label: 'Account',                path: '/account' },
    { label: 'Legacy NFL Analytics →', path: '/nfl'      },
  ],
};

function FooterCol({ title, items }) {
  const navigate = useNavigate();
  return (
    <Box>
      <Typography sx={{
        fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.08em',
        color: '#3a5878', mb: 1.5, textTransform: 'uppercase',
      }}>
        {title}
      </Typography>
      {items.map(item => (
        <Typography key={item.label} onClick={() => navigate(item.path)}
          sx={{
            fontSize: '0.83rem', color: '#4a6a88', mb: 0.75,
            cursor: 'pointer', transition: 'color 0.15s',
            letterSpacing: '-0.01em',
            '&:hover': { color: '#2ea84b' },
          }}>
          {item.label}
        </Typography>
      ))}
    </Box>
  );
}

function Footer() {
  const location = useLocation();
  const isLegacyAnalyticsPage = LEGACY_ANALYTICS_PREFIXES.some(prefix => location.pathname.startsWith(prefix));

  return (
    <Box sx={{
      borderTop: '1px solid rgba(26,46,68,0.6)',
      background: 'linear-gradient(180deg, rgba(5,11,20,0.0) 0%, rgba(5,11,20,0.98) 40%)',
      pt: 5, pb: 4, mt: 'auto',
    }}>
      {/* Thin green accent line */}
      <Box sx={{
        position: 'absolute',
        top: 0, left: '50%', transform: 'translateX(-50%)',
        width: '100%', height: '1px',
        background: 'linear-gradient(90deg, transparent 0%, rgba(46,168,75,0.25) 30%, rgba(46,168,75,0.25) 70%, transparent 100%)',
        pointerEvents: 'none',
      }} />

      <Container maxWidth="lg">
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr' },
          gap: { xs: 3, sm: 4 },
          mb: 4,
        }}>
          {/* Brand col */}
          <Box>
            <Typography variant="h6" sx={{
              fontWeight: 900, mb: 0.5, fontSize: '1rem', letterSpacing: '-0.02em',
            }}>
              🏈{' '}
              <span style={{
                background: 'linear-gradient(135deg, #2ea84b 0%, #3ec45e 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>Football</span>{' '}
              <span style={{ color: '#e8f0f8' }}>for Nerds</span>
            </Typography>
            <Typography sx={{
              fontSize: '0.66rem', fontWeight: 800, letterSpacing: '0.06em',
              color: '#2ea84b', mb: 1.25,
            }}>
              COMMUNICATE. MEASURE. DEVELOP. UNDERSTAND. SHOWCASE.
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', color: '#3a5878', lineHeight: 1.7, maxWidth: 280 }}>
              Tools for football programs, coaches, athletes, and families.
            </Typography>
            {/* Preserved verbatim, not deleted — only shown while actually
                browsing the legacy analytics pages this data comes from. */}
            {isLegacyAnalyticsPage && (
              <Typography sx={{ fontSize: '0.72rem', color: 'rgba(245,166,35,0.25)', mt: 2, letterSpacing: '-0.005em' }}>
                Data powered by{' '}
                <span style={{ color: '#3a6a88' }}>nflverse</span>
              </Typography>
            )}
          </Box>

          <FooterCol title="Platform" items={LINKS.PLATFORM} />
          <FooterCol title="More" items={LINKS.MORE} />
        </Box>

        {/* Bottom bar */}
        <Box sx={{
          borderTop: '1px solid rgba(26,46,68,0.5)',
          pt: 2.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
        }}>
          <Typography sx={{ fontSize: '0.72rem', color: 'rgba(245,166,35,0.25)', letterSpacing: '-0.005em' }}>
            © 2026 Football for Nerds. All rights reserved.
          </Typography>
          <Typography sx={{ fontSize: '0.72rem', color: 'rgba(245,166,35,0.25)', letterSpacing: '-0.005em' }}>
            Not affiliated with the NFL or any team.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export default Footer;
